/* eslint-disable no-console */
import * as pino from 'pino';

/**
 * Logger de aplicação (universal).
 *
 * No servidor usa pino (log estruturado). No cliente, o pino era empacotado no
 * bundle do browser (~7.5KB) apenas para, no fim, delegar ao `console` (pino em
 * modo browser). Como o branch `import.meta.server` é eliminado no build do
 * client, o `import * as pino` fica sem uso e o Rollup remove o pino do bundle
 * do browser. O shim de console espelha o comportamento padrão do pino-browser
 * (pass-through para o console; nível 'info' → debug/trace silenciados), então
 * os call sites não mudam.
 */
type LogFn = (...args: unknown[]) => void;

interface AppLogger {
	info: LogFn;
	warn: LogFn;
	error: LogFn;
	debug: LogFn;
	trace: LogFn;
	fatal: LogFn;
}

function createBrowserLogger(): AppLogger {
	const toConsole =
		(method: 'info' | 'warn' | 'error' | 'debug' | 'log'): LogFn =>
		(...args) => {
			(console[method] ?? console.log)(...args);
		};
	const noop: LogFn = () => {};

	return {
		info: toConsole('info'),
		warn: toConsole('warn'),
		error: toConsole('error'),
		// pino default level é 'info' → debug/trace já ficam silenciados (paridade)
		debug: noop,
		trace: noop,
		fatal: toConsole('error')
	};
}

let logger: AppLogger;

if (import.meta.server) {
	logger = pino.default() as unknown as AppLogger;
} else {
	logger = createBrowserLogger();
}

export { logger };
